/* eslint-env browser */
import React, { useEffect, useMemo, createRef, useState } from 'react';
import { throttle as _throttle } from 'lodash';
import Section from '@latitude/section-rebrand';
import Slider from '@latitude/slider';
import Slide from '@latitude/slide';
import { Box } from '@latitude/box';
import Button from '@latitude/button-rebrand';
import { AnalyticsLocationProvider } from 'latitude-analytics';
import styled from 'styled-components';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import Card from '../../Card/CardV2';
import Typography from '@latitude/typography';
import { debounce as _debounce } from 'lodash';
import { BREAKPOINT, GUTTER, BUTTON_THEME } from '@/utils/constants';
import Modal from '@/components/ModalRebrand/ModalRebrand';
import './_card_collection.scss';
import { REBRAND_COLOR } from '@latitude/core/utils/constants';
import { getCdrEntryBindingRichTextOptions } from '@/utils/contentfulRichTextBindings';

const CardCollection = props => {
  const {
    title,
    isStacked,
    cards,
    stackedCol = 3,
    cardTitleSize = 'Heading 4',
    cardDescriptionSize = 'Subtitle',
    disclaimer,
    theme = 'Grey',
    showEligible,
    button
  } = props?.data;

  const [showModal, setShowModal] = useState(false);

  const CardStackedWrapper = styled(Box)`
    overflow: hidden;
    gap: 30px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex-flow: row wrap;
    @media (max-width: ${BREAKPOINT.MD}) {
      padding: 0 15px;
    }

    & > * {
      flex: 1 1 100%;
    }

    @media (min-width: ${BREAKPOINT.MD}) {
      & > * {
        flex: 1 1 calc(${100 / (stackedCol - 2)}% - ${GUTTER.G15} * 2);
        max-width: calc(
          ${100 / (stackedCol - 2)}% - ${GUTTER.G15} * 2
        ) !important;
      }
    }
    @media (min-width: ${BREAKPOINT.LG}) {
      & > * {
        flex: 1 1 calc(${100 / (stackedCol - 1)}% - ${GUTTER.G15} * 2);
        max-width: calc(
          ${100 / (stackedCol - 1)}% - ${GUTTER.G15} * 2
        ) !important;
      }
    }
    @media (min-width: ${BREAKPOINT.XL}) {
      & > * {
        flex: 1 1 calc(${100 / stackedCol}% - ${GUTTER.G15} * 2);
        max-width: calc(${100 / stackedCol}%) !important;
      }
    }
  `;

  const sliderSettings = {
    responsiveSettings: [
      {
        breakpoint: 10000,
        settings: {
          slidesToShow: cards?.length <= 3 ? 3 : 4.6,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 1100,
        settings: {
          slidesToShow: 2.5,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };

  const TITLE_OPTIONS = getCdrEntryBindingRichTextOptions({
    renderNode: {
      [BLOCKS.HEADING_3]: (_node, children) => (
        <Typography.SH1
          color={
            props?.textColor === 'indigo'
              ? REBRAND_COLOR.INDIGO
              : REBRAND_COLOR.BLACK
          }
        >
          {children}
        </Typography.SH1>
      )
    }
  });

  const titleRef = useMemo(() => {
    const refs = {};
    cards?.forEach((card, index) => {
      refs[card.id] = createRef();
    });
    return refs;
  }, [cards]);

  const setTitleHeight = height => {
    Object.keys(titleRef).forEach(key => {
      const ref = titleRef[key];
      if (ref && ref.current) {
        ref.current.style.minHeight = height ? `${height}px` : 'auto';
      }
    });
  };

  const equalizeTitleHeight = () => {
    let max = 0;
    Object.keys(titleRef).forEach(key => {
      const element = titleRef[key]?.current;
      if (element && element.offsetHeight > max) {
        max = element.offsetHeight;
      }
    });
    setTitleHeight(max);
  };

  const bodyRef = useMemo(() => {
    const refs = {};
    cards?.forEach((card, index) => {
      refs[card.id] = createRef();
    });
    return refs;
  }, [cards]);

  const setBodyHeight = height => {
    Object.keys(bodyRef).forEach(key => {
      const ref = bodyRef[key];
      if (ref && ref.current) {
        ref.current.style.minHeight = height ? `${height}px` : 'auto';
      }
    });
  };

  const equalizeBodyHeight = () => {
    let max = 0;
    Object.keys(bodyRef).forEach(key => {
      const element = bodyRef[key]?.current;
      if (element && element.offsetHeight > max) {
        max = element.offsetHeight;
      }
    });
    setBodyHeight(max);
  };

  const cardRef = useMemo(() => {
    const refs = {};
    cards?.forEach((card, index) => {
      refs[card.id] = createRef();
    });
    return refs;
  }, [cards]);

  const setCardHeight = height => {
    Object.keys(cardRef).forEach(key => {
      const ref = cardRef[key];
      if (ref && ref.current) {
        ref.current.style.minHeight = height ? `${height}px` : 'auto';
      }
    });
  };

  const equalizeCardHeight = () => {
    let max = 0;
    Object.keys(cardRef).forEach(key => {
      const element = cardRef[key]?.current;
      if (element && element.offsetHeight > max) {
        max = element.offsetHeight;
      }
    });
    setCardHeight(max);
  };

  const handleWindowResize = () => {
    setTitleHeight();
    equalizeTitleHeight();
    setBodyHeight();
    equalizeBodyHeight();
    setCardHeight();
    equalizeCardHeight();
  };

  const debouncedHandleResize = _debounce(handleWindowResize, 100);

  useEffect(() => {
    equalizeTitleHeight();
    equalizeBodyHeight();
    equalizeCardHeight();
    window.addEventListener('resize', debouncedHandleResize);
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, [cards, titleRef, bodyRef]);

  useEffect(() => {
    debouncedHandleResize();
  }, []);

  const getButtonType = theme => {
    if (theme === 'Blue') return 'secondary-blue';
    else if (theme === 'Outline') return 'secondary-white';
    return 'primary';
  };

  return (
    <AnalyticsLocationProvider location={title}>
      <div
        className="card-collection-outer-wrapper"
        style={{
          backgroundColor:
            theme?.toLowerCase() === 'blue'
              ? REBRAND_COLOR.MID_BLUE_20
              : theme?.toLowerCase() === 'grey'
              ? REBRAND_COLOR.STONE
              : theme?.toLowerCase() === 'white'
              ? REBRAND_COLOR.WHITE
              : 'transparent'
        }}
      >
        <Section id={props?.id || ''} className="card-collection-v2">
          {title && (
            <div className="card-collection-v2-heading">
              {documentToReactComponents(title, TITLE_OPTIONS)}
            </div>
          )}

          {isStacked ? (
            <CardStackedWrapper columns={stackedCol}>
              {cards &&
                cards.map((card, i) => {
                  return (
                    <Card
                      location={title}
                      data={card}
                      cardTitleSize={cardTitleSize}
                      cardDescriptionSize={cardDescriptionSize}
                      theme={theme}
                      cardRef={cardRef}
                      cardTitleRef={titleRef}
                      cardBodyRef={bodyRef}
                      category={props?.category || 'text-link'}
                      action={props?.action || 'internal-link'}
                      stackedCol={stackedCol}
                      textColor={props?.textColor}
                    />
                  );
                })}
            </CardStackedWrapper>
          ) : (
            <Slider
              {...sliderSettings}
              className={`slider slider--${cards?.length > 3 && 'overflow'}`}
              css={`
                .slick-dots li button:before {
                  content: '' !important;
                  display: block !important;
                  width: 12px !important;
                  height: 12px !important;
                  background-color: ${theme?.toLowerCase() === 'grey'
                    ? REBRAND_COLOR.WHITE
                    : REBRAND_COLOR.STONE} !important;
                  border-radius: 50% !important;
                  opacity: 1 !important;
                  border: none !important;
                }
              `}
            >
              {cards &&
                cards.map((card, i) => {
                  return (
                    <Slide key={i}>
                      <Card
                        location={title}
                        data={card}
                        cardTitleSize={cardTitleSize}
                        cardDescriptionSize={cardDescriptionSize}
                        theme={theme}
                        cardRef={cardRef}
                        cardTitleRef={titleRef}
                        cardBodyRef={bodyRef}
                        category={props?.category || 'button-link'}
                        action={props?.action || 'internal-link'}
                        cardNumber={cards?.length}
                        textColor={props?.textColor}
                      />
                    </Slide>
                  );
                })}
            </Slider>
          )}
          {showEligible && (
            <div className="card-collection-v2-link">
              <Typography.LinkLarge
                color={REBRAND_COLOR.INDIGO}
                href="#"
                noStyle
                onClick={e => {
                  setShowModal(true);
                  e.preventDefault();
                }}
                trackEventData={{
                  // category: CALCULATOR_TRACKING.CATEGORY,
                  // action: CALCULATOR_TRACKING.ACTION,
                  label: 'Check your eligibility'
                }}
              >
                Check your eligibility
              </Typography.LinkLarge>
            </div>
          )}

          <Modal
            isOpen={showModal}
            onRequestClose={() => setShowModal(false)}
            title={
              <Typography.SH4 semibold color={REBRAND_COLOR.BLACK}>
                Are you eligible?
              </Typography.SH4>
            }
            width="fit-content"
            content={
              <>
                <Typography.SH4 semibold color={REBRAND_COLOR.BLACK}>
                  To be eligible for a Personal Loan you must
                </Typography.SH4>
                <div
                  css={`
                    && li {
                      padding-left: 8px;
                    }
                    && li::marker {
                      color: #63b8ff;
                      width: 8px;
                      height: 8px;
                    }
                  `}
                >
                  <ul
                    css={`
                      display: flex;
                      flex-direction: column;
                      gap: 16px;
                      padding-left: 1rem !important;
                      color: #000;
                    `}
                  >
                    <li>Be 18 years or over</li>
                    <li>
                      Be able to demonstrate a good credit history for the last
                      5 years
                    </li>
                    <li>Be a permanent Australian resident</li>
                    <li>Be free from bankruptcy for the last 7 years</li>
                    <li>
                      Be currently employed and earning at least $25,000 gross
                      per annum
                    </li>
                  </ul>
                </div>

                <Button
                  variant="primary"
                  onClick={() => setShowModal(false)}
                  size="lg"
                  ctaText="Close"
                  targetBlank="_blank"
                  style={{ width: '100%' }}
                />
              </>
            }
            ariaHideApp={false}
          />
          {(disclaimer || button?.url) && (
            <div className="card-collection-v2-wrapper">
              {button?.url && (
                <Button
                  variant={getButtonType(button?.theme)}
                  size="lg"
                  trackEventData={{
                    label: button?.text,
                    location: title,
                    category: 'cta',
                    action: 'quote-link'
                  }}
                  ctaText={button?.text}
                  ctaUrl={button?.url}
                />
              )}
              {disclaimer && (
                <div className="card-collection-v2-disclaimer">
                  {documentToReactComponents(
                    disclaimer,
                    getCdrEntryBindingRichTextOptions()
                  )}
                </div>
              )}
            </div>
          )}
        </Section>
      </div>
    </AnalyticsLocationProvider>
  );
};

export default CardCollection;
